import React, { useCallback, useMemo } from 'react'

import { Box, Typography } from '@material-ui/core'
import AvatarWithTitle, { Title } from '@scoir/avatar-with-title'
import { AddToCompare } from '@scoir/college-compare'
import CollegeCrest from '@scoir/college-crest'
import { ErrorBoundary } from '@scoir/error-reporter'
import qs from '@scoir/query-string'
import { Metadata } from '@scoir/tracking'
import { TrackedImpression } from '@scoir/tracking'
import { get, noop } from 'lodash'

import { getMetaDataParams } from 'common/click-tracking-params'
import { FIELDS } from 'common/metadata-constants'
import { DOMAIN_TYPES, EVENT_TYPES } from 'common/track-event-constants'
import AddToMyCollegesSwitch from 'components/AddToMyCollegesSwitch'
import AdmissionIntelligence from 'components/AdmissionIntelligence'
import GeographicLocation from 'components/GeographicLocation'

import LinkListItem from '../../LinkListItem'
import { useClickTracking } from '../use-click-tracking'
import { useOnSaveSearchHistory } from '../use-on-save-search-history'

export const getTo = (scid: number | string, linkParams?: object) => {
  const params = linkParams ? `?${qs.stringify(linkParams)}` : ''
  const url = `${import.meta.env.BASE_URL}/colleges/${scid}/overview${params}`
  return url
}

export interface IResultComponentProps {
  id?: string
  scid: number
  name: string
  city: string
  state: string
  linkParams?: object
  type?: string
  additionalMetadata?: object
  listItemProps?: object
}

const ResultComponent = ({
  id,
  scid,
  name,
  city,
  state,
  linkParams,
  additionalMetadata = {},
  listItemProps,
}: IResultComponentProps) => {
  const trackClickFn = useClickTracking(EVENT_TYPES.COLLEGE_CLICK)
  const metadataParams = getMetaDataParams('Follow', 'GlobalSearch', scid)
  const hoverText = `Go to college profile for ${name}`
  const to = getTo(scid, linkParams)
  const listItemClick = useMemo(() => get(listItemProps, 'onClick', noop), [listItemProps])

  const onSaveSearchHistory = useOnSaveSearchHistory({ Scid: scid })

  const onClick = useCallback(() => {
    onSaveSearchHistory()
    trackClickFn()
    listItemClick()
  }, [trackClickFn, onSaveSearchHistory, listItemClick])

  return (
    <ErrorBoundary>
      <Metadata
        value={{
          [FIELDS.SCID]: scid,
          [FIELDS.DOMAIN_TYPE]: DOMAIN_TYPES.IMPRESSION,
          ...additionalMetadata,
        }}
      >
        <LinkListItem
          to={to}
          hoverText={hoverText}
          onClick={onClick}
          {...listItemProps}
          id={id}
        >
          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            aria-label={name}
          >
            <TrackedImpression>
              <AvatarWithTitle
                ml={0}
                avatar={
                  <CollegeCrest
                    alt={`${name} logo`}
                    scid={scid}
                    size="sm"
                  />
                }
                title={<Title>{name}</Title>}
              >
                <Typography
                  variant={'body2'}
                  component="small"
                  color="textSecondary"
                >
                  <GeographicLocation
                    city={city}
                    state={state}
                    country={'null'}
                  />
                </Typography>
              </AvatarWithTitle>
            </TrackedImpression>
            <Box
              minWidth="84px"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation()
                e.preventDefault()
              }}
            >
              <AdmissionIntelligence scid={scid} />
              <AddToCompare
                scid={scid}
                collegeName={name}
                iconOnly
              />
              <Metadata value={metadataParams}>
                <AddToMyCollegesSwitch
                  displayText={false}
                  scid={scid}
                  name={name}
                />
              </Metadata>
            </Box>
          </Box>
        </LinkListItem>
      </Metadata>
    </ErrorBoundary>
  )
}

export default ResultComponent
